import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/joy/Box';
import IconButton from '@mui/joy/IconButton';
import Typography from '@mui/joy/Typography';
import LinearProgress from '@mui/joy/LinearProgress';
import MicIcon from '@mui/icons-material/Mic';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import CheckIcon from '@mui/icons-material/Check';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';

export type VoiceRecorderPhase = 'idle' | 'recording' | 'preview';

interface VoiceRecorderProps {
  phase: VoiceRecorderPhase;
  durationMs: number;
  previewAudioUrl: string | null;
  onStartRecording: () => void;
  onFinishRecording: () => void;
  onDiscardRecording: () => void;
  onSendRecording: () => void;
  onDiscardPreview: () => void;
  disabled?: boolean;
  isSending?: boolean;
}

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  phase,
  durationMs,
  previewAudioUrl,
  onStartRecording,
  onFinishRecording,
  onDiscardRecording,
  onSendRecording,
  onDiscardPreview,
  disabled = false,
  isSending = false,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    setIsPlaying(false);
    const el = audioRef.current;
    if (el) {
      el.pause();
      el.currentTime = 0;
    }
  }, [previewAudioUrl]);

  const formatDuration = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const togglePlayback = (): void => {
    const el = audioRef.current;
    if (!el || !previewAudioUrl) return;
    if (isPlaying) {
      el.pause();
    } else {
      void el.play().catch(() => setIsPlaying(false));
    }
  };

  if (phase === 'idle') {
    return (
      <IconButton
        variant="plain"
        color="neutral"
        disabled={disabled}
        onClick={onStartRecording}
      >
        <MicIcon />
      </IconButton>
    );
  }

  if (phase === 'recording') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.5,
          borderRadius: 'sm',
          bgcolor: 'danger.softBg',
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'danger.solidBg',
            animation: 'pulse 1s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.4 },
            },
          }}
        />
        <Typography level="body-sm" sx={{ color: 'danger.solidColor', fontWeight: 'lg' }}>
          {formatDuration(durationMs)}
        </Typography>
        <IconButton
          size="sm"
          variant="soft"
          color="success"
          onClick={onFinishRecording}
          title="Listo"
        >
          <CheckIcon />
        </IconButton>
        <IconButton
          size="sm"
          variant="plain"
          color="danger"
          onClick={onDiscardRecording}
          title="Descartar"
        >
          <CloseIcon />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 0.5,
        borderRadius: 'sm',
        bgcolor: 'neutral.softBg',
        minWidth: 160,
        flexWrap: 'wrap',
      }}
    >
      {previewAudioUrl ? (
        <audio
          ref={audioRef}
          src={previewAudioUrl}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
          style={{ display: 'none' }}
        />
      ) : null}
      <IconButton
        size="sm"
        variant="soft"
        color="primary"
        onClick={togglePlayback}
        disabled={!previewAudioUrl}
        title={isPlaying ? 'Pausar' : 'Escuchar'}
      >
        {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
      </IconButton>
      <Typography level="body-sm" sx={{ color: 'neutral.600', minWidth: 40 }}>
        {formatDuration(durationMs)}
      </Typography>
      <LinearProgress
        determinate
        value={Math.min(durationMs / 6000, 1) * 100}
        sx={{ flex: 1, minWidth: 48 }}
      />
      <IconButton
        size="sm"
        variant="plain"
        color="danger"
        onClick={onDiscardPreview}
        disabled={isSending}
        title="Descartar"
      >
        <CloseIcon />
      </IconButton>
      <IconButton
        size="sm"
        variant="soft"
        color="primary"
        onClick={onSendRecording}
        loading={isSending}
        disabled={isSending || !previewAudioUrl}
        title="Enviar"
      >
        <SendIcon />
      </IconButton>
    </Box>
  );
};

export default VoiceRecorder;
